(() => {
  'use strict';

  const PATCH_VERSION = 'fix5-cart-reliability-2026-08-06';
  const RUNTIME_KEY = '__gdcCartReliabilityRuntime';
  const existingRuntime = window[RUNTIME_KEY];

  if (existingRuntime?.initialized) {
    existingRuntime.assetLoads += 1;
    return;
  }

  const runtime = {
    version: PATCH_VERSION,
    initialized: true,
    assetLoads: 1,
    loggedErrors: existingRuntime?.loggedErrors || new Set(),
    checkoutListenerBound: false,
    cartQuantityIntentListenerBound: false,
    cartQuantityIntents: new WeakMap(),
    cartUpdateTimeline: [],
    patchPromisesScheduled: false,
    diagnostics: {
      cartPrototypePatches: 0,
      cartItemsPrototypePatches: 0,
      cartUpdateRequests: 0,
      cartUpdateFailures: 0,
      cartUpdateSectionFailures: 0,
      cartQuantityIntents: 0,
      productPrototypePatches: 0,
      checkoutListeners: 0,
      pageshowListeners: 0,
      mutationObservers: 0,
      recoveryStarts: 0,
      recoveryFallbacks: 0,
    },
  };
  window[RUNTIME_KEY] = runtime;

  const reportError = (code, error) => {
    if (runtime.loggedErrors.has(code)) return;
    runtime.loggedErrors.add(code);
    console.error(`[GutsDog cart] ${code}`, {
      patch: PATCH_VERSION,
      message: error instanceof Error ? error.message : String(error || code),
    });
  };

  const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  const recordCartUpdate = (entry) => {
    runtime.cartUpdateTimeline.push({
      at: Date.now(),
      ...entry,
    });
    if (runtime.cartUpdateTimeline.length > 20) runtime.cartUpdateTimeline.shift();
  };

  const parseSection = (html, selector = '.shopify-section') => {
    if (typeof html !== 'string' || html.trim() === '') {
      throw new Error('Section HTML is missing or empty');
    }

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const section = documentFragment.querySelector(selector);
    if (!section) throw new Error(`Section selector not found: ${selector}`);
    return section;
  };

  const getRecoveryNotice = (drawer) => {
    let notice = drawer.querySelector('[data-gdc-cart-recovery]');
    if (notice) return notice;

    notice = document.createElement('div');
    notice.className = 'gdc-cart-recovery';
    notice.hidden = true;
    notice.dataset.gdcCartRecovery = '';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.innerHTML = `
      <p class="gdc-cart-recovery__message">Item added to cart.</p>
      <div class="gdc-cart-recovery__actions">
        <a class="button button--secondary" href="/cart">View cart</a>
        <a class="button gdc-cart-recovery__checkout" href="/checkout">Checkout</a>
      </div>
    `;
    drawer.appendChild(notice);
    return notice;
  };

  const hideRecoveryNotice = (drawer) => {
    const notice = drawer.querySelector('[data-gdc-cart-recovery]');
    if (notice) notice.hidden = true;
  };

  const showRecoveryNotice = (drawer, reason) => {
    const notice = getRecoveryNotice(drawer);
    runtime.diagnostics.recoveryFallbacks += 1;
    notice.hidden = false;
    drawer.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    notice.querySelector('a')?.focus();

    drawer.dispatchEvent(
      new CustomEvent('gdc:cart-render-failed', {
        bubbles: true,
        detail: { reason, cartAddSucceeded: true },
      })
    );
  };

  const renderCartSections = (drawer, state) => {
    if (!state || typeof state !== 'object') throw new Error('Cart response is missing');
    if (!state.sections || typeof state.sections !== 'object') {
      throw new Error('Cart section response is missing');
    }

    const drawerHtml = state.sections['cart-drawer'];
    const drawerDestination = document.querySelector('#CartDrawer');
    if (!drawerDestination) throw new Error('#CartDrawer is not mounted');

    const drawerSource = parseSection(drawerHtml, '#CartDrawer');
    const iconHtml = state.sections['cart-icon-bubble'];
    const iconDestination = document.getElementById('cart-icon-bubble');
    const iconSource =
      typeof iconHtml === 'string' && iconHtml.trim() !== ''
        ? parseSection(iconHtml, '.shopify-section')
        : null;

    drawerDestination.innerHTML = drawerSource.innerHTML;
    if (iconDestination && iconSource) iconDestination.innerHTML = iconSource.innerHTML;

    drawer.productId = state.id;
    drawer.classList.remove('is-empty');
    drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');

    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (!overlay) throw new Error('#CartDrawer-Overlay is missing after render');
    overlay.addEventListener('click', drawer.close.bind(drawer), { once: true });

    hideRecoveryNotice(drawer);
    window.setTimeout(() => drawer.open(), 0);
  };

  const getRetryDelay = (response) => {
    const retryAfter = response?.headers?.get?.('Retry-After');
    if (retryAfter) {
      const seconds = Number(retryAfter);
      if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
      const retryDate = Date.parse(retryAfter);
      if (Number.isFinite(retryDate)) return Math.max(0, retryDate - Date.now());
    }
    return 750 + Math.floor(Math.random() * 251);
  };

  const fetchCartSections = async (externalSignal) => {
    const cartUrl = window.routes?.cart_url || '/cart';
    const separator = cartUrl.includes('?') ? '&' : '?';
    const url = `${cartUrl}${separator}sections=cart-drawer,cart-icon-bubble`;
    let lastError;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 4000);
      const abortFromExternal = () => controller.abort();
      externalSignal?.addEventListener('abort', abortFromExternal, { once: true });

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
        });

        if (!response.ok) {
          const error = new Error(`Cart section refresh returned HTTP ${response.status}`);
          error.retryable = response.status === 429 || response.status >= 500;
          error.retryAfterMs = getRetryDelay(response);
          throw error;
        }

        const sections = await response.json();
        if (!sections || typeof sections !== 'object') {
          throw new Error('Cart section refresh returned invalid JSON');
        }
        return sections;
      } catch (error) {
        lastError = error;
        const retryable =
          error?.retryable || error?.name === 'AbortError' || error instanceof TypeError;
        if (externalSignal?.aborted || !retryable || attempt === 1) break;
        await delay(error?.retryAfterMs ?? 750 + Math.floor(Math.random() * 251));
      } finally {
        window.clearTimeout(timeout);
        externalSignal?.removeEventListener('abort', abortFromExternal);
      }
    }

    throw lastError || new Error('Cart section refresh failed');
  };

  const recoverCartDrawer = async (drawer, state, initialError, generation) => {
    const existing = drawer.__gdcRecoveryContext;
    if (existing?.generation === generation) return existing.promise;
    if (existing?.stateId && existing.stateId === String(state?.id || '')) {
      drawer.__gdcRenderGeneration = existing.generation;
      return existing.promise;
    }
    existing?.abortController.abort();

    const context = {
      generation,
      stateId: String(state?.id || ''),
      abortController: new AbortController(),
      promise: null,
    };
    runtime.diagnostics.recoveryStarts += 1;
    context.promise = (async () => {
      try {
        const sections = await fetchCartSections(context.abortController.signal);
        if (generation !== drawer.__gdcRenderGeneration) return;
        renderCartSections(drawer, { id: state?.id, sections });
      } catch (refreshError) {
        if (
          context.abortController.signal.aborted ||
          generation !== drawer.__gdcRenderGeneration
        ) {
          return;
        }
        reportError('drawer-render-recovery-failed', refreshError);
        showRecoveryNotice(drawer, initialError?.message || refreshError?.message || 'unknown');
      } finally {
        if (drawer.__gdcRecoveryContext === context) {
          drawer.__gdcRecoveryContext = null;
        }
      }
    })();
    drawer.__gdcRecoveryContext = context;
    return context.promise;
  };

  const patchCartDrawer = () => {
    const CartDrawer = customElements.get('cart-drawer');
    if (!CartDrawer || CartDrawer.prototype.__gdcReliabilityPatched) return;

    const prototype = CartDrawer.prototype;
    prototype.__gdcReliabilityPatched = {
      version: PATCH_VERSION,
      strategy: 'prototype-method-replacement',
    };
    runtime.diagnostics.cartPrototypePatches += 1;

    prototype.getSectionInnerHTML = (html, selector = '.shopify-section') =>
      parseSection(html, selector).innerHTML;
    prototype.getSectionDOM = (html, selector = '.shopify-section') =>
      parseSection(html, selector);

    prototype.renderContents = function renderContents(state) {
      this.__gdcRenderGeneration = (this.__gdcRenderGeneration || 0) + 1;
      const generation = this.__gdcRenderGeneration;
      this.__gdcRecoveryContext?.abortController.abort();
      try {
        renderCartSections(this, state);
      } catch (error) {
        reportError('drawer-render-failed', error);
        void recoverCartDrawer(this, state, error, generation);
      }
    };
  };

  const getCartUpdateErrorHost = (cartItems) => {
    const existing =
      document.getElementById('cart-errors') ||
      document.getElementById('CartDrawer-CartErrors');
    if (existing) return existing;

    const host = document.createElement('div');
    host.dataset.gdcCartUpdateError = '';
    host.setAttribute('role', 'alert');
    cartItems.append(host);
    return host;
  };

  const clearCartUpdateError = () => {
    document
      .querySelectorAll('#cart-errors, #CartDrawer-CartErrors, [data-gdc-cart-update-error]')
      .forEach((host) => host.replaceChildren());
    document.querySelectorAll('[data-gdc-cart-update-checkout-disabled]').forEach((button) => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      delete button.dataset.gdcCartUpdateCheckoutDisabled;
    });
  };

  const showCartUpdateError = (
    cartItems,
    message,
    totalMayBeStale = false,
    preserveConfirmedTotal = false
  ) => {
    const host = getCartUpdateErrorHost(cartItems);
    const reloadLink = document.createElement('a');
    reloadLink.href = window.routes?.cart_url || '/cart';
    reloadLink.className = 'link';
    reloadLink.textContent = 'Reload cart';
    host.replaceChildren(document.createTextNode(`${message} `), reloadLink);

    if (!totalMayBeStale) return;
    if (!preserveConfirmedTotal) {
      document
        .querySelectorAll('#CartDrawer .totals__total-value, #main-cart-footer .totals__total-value')
        .forEach((total) => {
          total.textContent = 'Reload cart to confirm total.';
          total.dataset.gdcCartTotalStale = '';
        });
    }
    document.querySelectorAll('#CartDrawer-Checkout, #checkout').forEach((button) => {
      if (button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.gdcCartUpdateCheckoutDisabled = '';
    });
  };

  const getCartLineKey = (cartItems, line, event) => {
    const target = event?.target;
    const currentTarget = event?.currentTarget;
    const row =
      target?.closest?.('.cart-item') ||
      currentTarget?.closest?.('.cart-item') ||
      cartItems.querySelector(`#CartItem-${CSS.escape(String(line))}`) ||
      cartItems.querySelector(`#CartDrawer-Item-${CSS.escape(String(line))}`);
    const explicitKey =
      target?.dataset?.lineKey ||
      currentTarget?.dataset?.lineKey ||
      row?.dataset?.lineKey;
    if (explicitKey) return explicitKey;

    const removeLink = row?.querySelector('cart-remove-button a[href]');
    if (!removeLink?.href) return '';
    try {
      return new URL(removeLink.href, window.location.origin).searchParams.get('id') || '';
    } catch {
      return '';
    }
  };

  const setCartUpdateControlsLocked = (cartItems, locked) => {
    if (locked) {
      cartItems.querySelectorAll('quantity-input button, .quantity__input, cart-remove-button button, cart-remove-button a').forEach(
        (control) => {
          if (control.disabled || control.getAttribute('aria-disabled') === 'true') return;
          if ('disabled' in control) control.disabled = true;
          control.setAttribute('aria-disabled', 'true');
          control.dataset.gdcCartUpdateLocked = '';
        }
      );
      return;
    }

    document.querySelectorAll('[data-gdc-cart-update-locked]').forEach((control) => {
      if ('disabled' in control) control.disabled = false;
      control.removeAttribute('aria-disabled');
      delete control.dataset.gdcCartUpdateLocked;
    });
  };

  const clearCartQuantityIntent = (input, outcome) => {
    const intent = input && runtime.cartQuantityIntents.get(input);
    if (!intent) return null;
    window.clearTimeout(intent.watchdog);
    runtime.cartQuantityIntents.delete(input);
    if (outcome) {
      recordCartUpdate({
        type: 'quantity-intent',
        intentId: intent.id,
        outcome,
        durationMs: Math.round(performance.now() - intent.startedAt),
      });
    }
    return intent;
  };

  const bindCartQuantityIntentListener = () => {
    if (runtime.cartQuantityIntentListenerBound) return;
    runtime.cartQuantityIntentListenerBound = true;

    document.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!input?.matches?.('.quantity__input')) return;
        const cartItems = input.closest('cart-items, cart-drawer-items');
        if (!cartItems || typeof cartItems.enableLoading !== 'function') return;

        const requestedQuantity = Number.parseInt(input.value, 10);
        const confirmedQuantity = Number.parseInt(
          input.getAttribute('value') ?? input.defaultValue,
          10
        );
        if (!Number.isFinite(requestedQuantity) || requestedQuantity === confirmedQuantity) return;

        const existing = runtime.cartQuantityIntents.get(input);
        if (existing) window.clearTimeout(existing.watchdog);
        const intent = {
          id: (runtime.cartQuantityIntentGeneration || 0) + 1,
          requestedQuantity,
          confirmedQuantity,
          startedAt: performance.now(),
          claimed: false,
          watchdog: null,
        };
        runtime.cartQuantityIntentGeneration = intent.id;
        runtime.cartQuantityIntents.set(input, intent);
        runtime.diagnostics.cartQuantityIntents += 1;
        input.value = String(confirmedQuantity);
        queueMicrotask(() => {
          if (runtime.cartQuantityIntents.get(input) === intent && !intent.claimed) {
            input.value = String(confirmedQuantity);
          }
        });
        cartItems.enableLoading(input.dataset.index);
        clearCartUpdateError();
        recordCartUpdate({
          type: 'quantity-intent',
          intentId: intent.id,
          line: input.dataset.index,
          requestedQuantity,
          outcome: 'feedback-visible',
        });

        intent.watchdog = window.setTimeout(() => {
          if (runtime.cartQuantityIntents.get(input) !== intent || intent.claimed) return;
          input.value = String(confirmedQuantity);
          clearCartQuantityIntent(input, 'debounced-handler-missing');
          cartItems.disableLoading();
          showCartUpdateError(cartItems, "Couldn't update cart. Try again.");
        }, 1500);
      },
      true
    );
  };

  const renderCartUpdateSections = (cartItems, state, sections) => {
    const rendered = [];
    const failed = [];

    sections.forEach((section) => {
      try {
        const destinationRoot = document.getElementById(section.id);
        if (!destinationRoot) throw new Error(`Destination section is missing: ${section.id}`);
        const destination = section.selector
          ? destinationRoot.querySelector(section.selector)
          : destinationRoot;
        if (!destination) {
          throw new Error(`Destination selector is missing: ${section.selector}`);
        }
        const sourceHtml = state.sections?.[section.section];
        const source = parseSection(sourceHtml, section.selector || '.shopify-section');
        destination.innerHTML = source.innerHTML;
        rendered.push(section.section);
      } catch (error) {
        runtime.diagnostics.cartUpdateSectionFailures += 1;
        failed.push({ section: section.section, message: error.message });
        reportError(`cart-update-section-${section.section}`, error);
        if (section.section === 'cart-icon-bubble') {
          const icon = document.getElementById('cart-icon-bubble');
          const sourceHtml = state.sections?.[section.section];
          const iconSource =
            typeof sourceHtml === 'string' && sourceHtml.trim() !== ''
              ? new DOMParser().parseFromString(sourceHtml, 'text/html').querySelector('#cart-icon-bubble')
              : null;
          const countBubble = icon?.querySelector('.cart-count-bubble');
          const plainSection = icon?.querySelector('.shopify-section');
          const directCount = icon?.querySelector(':scope > span') || icon?.querySelector('span');
          if (icon && iconSource) {
            icon.innerHTML = iconSource.innerHTML;
            rendered.push(section.section);
          } else if (countBubble) {
            const countNodes = countBubble.querySelectorAll('span');
            if (countNodes[0]) countNodes[0].textContent = String(state.item_count);
            if (countNodes[1]) {
              countNodes[1].textContent = `${state.item_count} item${
                state.item_count === 1 ? '' : 's'
              }`;
            }
            countBubble.hidden = state.item_count === 0;
            rendered.push(section.section);
          } else if (plainSection) {
            plainSection.textContent = String(state.item_count);
            rendered.push(section.section);
          } else if (directCount) {
            directCount.textContent = String(state.item_count);
            rendered.push(section.section);
          }
        }
      }
    });

    const requiredDestinationIds =
      cartItems.tagName === 'CART-DRAWER-ITEMS'
        ? ['CartDrawer']
        : ['main-cart-items', 'main-cart-footer'];
    return {
      rendered,
      failed,
      criticalFailure: requiredDestinationIds.some((id) => {
        const descriptor = sections.find((section) => section.id === id);
        return !descriptor || !rendered.includes(descriptor.section);
      }),
    };
  };

  const patchCartItems = () => {
    const CartItems = customElements.get('cart-items');
    if (!CartItems || CartItems.prototype.__gdcQuantityReliabilityPatched) return;

    const prototype = CartItems.prototype;
    prototype.__gdcQuantityReliabilityPatched = {
      version: PATCH_VERSION,
      strategy: 'prototype-method-replacement',
    };
    runtime.diagnostics.cartItemsPrototypePatches += 1;

    prototype.resetQuantityInput = function resetQuantityInput(id, inputHint) {
      const input =
        inputHint ||
        this.querySelector(`#Quantity-${CSS.escape(String(id))}`) ||
        this.querySelector(`#Drawer-quantity-${CSS.escape(String(id))}`);
      if (!input) return false;
      input.value = input.getAttribute('value') ?? input.defaultValue ?? input.value;
      this.isEnterPressed = false;
      return true;
    };

    prototype.validateQuantity = function validateQuantity(event) {
      const input = event?.target;
      if (!input) return;
      const intent = runtime.cartQuantityIntents.get(input);
      if (intent) {
        intent.claimed = true;
        window.clearTimeout(intent.watchdog);
      }
      const inputValue = intent?.requestedQuantity ?? Number.parseInt(input.value, 10);
      const index = input.dataset.index;
      const minimum = Number.parseInt(input.dataset.min, 10);
      const maximum = Number.parseInt(input.max, 10);
      const step = Number.parseInt(input.step, 10) || 1;
      let message = '';

      if (!Number.isFinite(inputValue) || inputValue < 0) {
        message = window.cartStrings?.error || 'Cart update failed.';
      } else if (inputValue !== 0 && Number.isFinite(minimum) && inputValue < minimum) {
        message = window.quickOrderListStrings.min_error.replace('[min]', minimum);
      } else if (Number.isFinite(maximum) && inputValue > maximum) {
        message = window.quickOrderListStrings.max_error.replace('[max]', maximum);
      } else if (inputValue !== 0 && inputValue % step !== 0) {
        message = window.quickOrderListStrings.step_error.replace('[step]', step);
      }

      if (message) {
        input.setCustomValidity(message);
        input.reportValidity();
        this.resetQuantityInput(index, input);
        clearCartQuantityIntent(input, 'validation-failed');
        this.disableLoading();
        input.select?.();
        return;
      }

      input.setCustomValidity('');
      input.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement?.getAttribute?.('name') || input.name,
        input.dataset.quantityVariantId,
        intent
      );
    };

    prototype.updateLiveRegions = function updateLiveRegions(line, message) {
      const lineItemError =
        document.getElementById(`Line-item-error-${line}`) ||
        document.getElementById(`CartDrawer-LineItemError-${line}`);
      const errorText = lineItemError?.querySelector('.cart-item__error-text');
      if (errorText) errorText.textContent = message || '';

      const lineStatus =
        (this.lineItemStatusElement?.isConnected && this.lineItemStatusElement) ||
        document.getElementById('shopping-cart-line-item-status') ||
        document.getElementById('CartDrawer-LineItemStatus');
      lineStatus?.setAttribute('aria-hidden', 'true');

      const cartStatus =
        document.getElementById('cart-live-region-text') ||
        document.getElementById('CartDrawer-LiveRegionText');
      if (!cartStatus) return;
      cartStatus.setAttribute('aria-hidden', 'false');
      window.setTimeout(() => cartStatus.setAttribute('aria-hidden', 'true'), 1000);
    };

    prototype.enableLoading = function enableLoading(line) {
      document
        .querySelectorAll('#main-cart-items, #CartDrawer-CartItems')
        .forEach((root) => root.classList.add('cart__items--disabled'));
      this.querySelectorAll(
        `#CartItem-${CSS.escape(String(line))} .loading__spinner, #CartDrawer-Item-${CSS.escape(
          String(line)
        )} .loading__spinner`
      ).forEach((spinner) => spinner.classList.remove('hidden'));
      setCartUpdateControlsLocked(this, true);
      document.activeElement?.blur?.();

      const lineStatus =
        (this.lineItemStatusElement?.isConnected && this.lineItemStatusElement) ||
        document.getElementById('shopping-cart-line-item-status') ||
        document.getElementById('CartDrawer-LineItemStatus');
      if (lineStatus) {
        this.lineItemStatusElement = lineStatus;
        lineStatus.setAttribute('aria-hidden', 'false');
      }
    };

    prototype.disableLoading = function disableLoading() {
      document
        .querySelectorAll('#main-cart-items, #CartDrawer-CartItems')
        .forEach((root) => root.classList.remove('cart__items--disabled'));
      document
        .querySelectorAll('cart-items .loading__spinner, cart-drawer-items .loading__spinner')
        .forEach((spinner) => spinner.classList.add('hidden'));
      setCartUpdateControlsLocked(this, false);
      document
        .querySelectorAll('#shopping-cart-line-item-status, #CartDrawer-LineItemStatus')
        .forEach((status) => status.setAttribute('aria-hidden', 'true'));
    };

    prototype.updateQuantity = async function updateQuantity(
      line,
      quantity,
      event,
      name,
      variantId,
      intent
    ) {
      if (runtime.cartUpdateContext?.pending) {
        this.resetQuantityInput(line, event?.target?.matches?.('.quantity__input') ? event.target : null);
        clearCartQuantityIntent(event?.target, 'blocked-by-interaction-lock');
        this.disableLoading();
        showCartUpdateError(this, "Couldn't update cart. Try again.");
        return;
      }

      const generation = (runtime.cartUpdateGeneration || 0) + 1;
      runtime.cartUpdateGeneration = generation;
      const controller = new AbortController();
      const context = {
        generation,
        requestId: intent?.id || generation,
        pending: true,
        controller,
        startedAt: performance.now(),
        timedOut: false,
      };
      runtime.cartUpdateContext = context;
      const timeout = window.setTimeout(() => {
        context.timedOut = true;
        recordCartUpdate({
          type: 'cart-change',
          requestId: context.requestId,
          outcome: 'timeout',
          durationMs: Math.round(performance.now() - context.startedAt),
          retry: false,
        });
        controller.abort();
      }, 12000);
      const lineKey = getCartLineKey(this, line, event);
      let cartMayHaveChanged = false;

      this.enableLoading(line);
      clearCartUpdateError();
      runtime.diagnostics.cartUpdateRequests += 1;
      recordCartUpdate({
        type: 'cart-change',
        requestId: context.requestId,
        line,
        quantity,
        outcome: 'request-started',
        retry: false,
      });

      try {
        const sections = this.getSectionsToRender();
        if (!Array.isArray(sections) || sections.length === 0) {
          throw new Error('No cart sections are configured for update');
        }
        const body = {
          quantity,
          sections: [...new Set(sections.map((section) => section.section).filter(Boolean))],
          sections_url: window.location.pathname,
        };
        if (lineKey) body.id = lineKey;
        else body.line = Number.parseInt(line, 10);

        const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          ...fetchConfig(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        recordCartUpdate({
          type: 'cart-change',
          requestId: context.requestId,
          outcome: 'response-received',
          status: response.status,
          durationMs: Math.round(performance.now() - context.startedAt),
          retry: false,
        });
        const responseText = await response.text();
        if (!response.ok) {
          const error = new Error(`Cart change returned HTTP ${response.status}`);
          error.cartMayHaveChanged = response.status >= 500;
          throw error;
        }

        let state;
        try {
          state = JSON.parse(responseText);
        } catch {
          const error = new Error('Cart change returned invalid JSON');
          error.cartMayHaveChanged = true;
          throw error;
        }
        cartMayHaveChanged = true;
        if (state?.errors) {
          const error = new Error('Cart change was rejected');
          error.cartMayHaveChanged = false;
          error.userMessage =
            typeof state.errors === 'string' ? state.errors : window.cartStrings?.error;
          throw error;
        }
        if (generation !== runtime.cartUpdateGeneration) {
          recordCartUpdate({
            type: 'cart-change',
            requestId: context.requestId,
            outcome: 'stale-response-ignored',
            durationMs: Math.round(performance.now() - context.startedAt),
            retry: false,
          });
          this.resetQuantityInput(
            line,
            event?.target?.matches?.('.quantity__input') ? event.target : null
          );
          showCartUpdateError(this, "Couldn't update cart. Try again.");
          return;
        }

        this.classList.toggle('is-empty', state.item_count === 0);
        document
          .getElementById('main-cart-footer')
          ?.classList.toggle('is-empty', state.item_count === 0);
        document
          .querySelector('cart-drawer')
          ?.classList.toggle('is-empty', state.item_count === 0);

        const renderResult = renderCartUpdateSections(this, state, sections);
        if (renderResult.criticalFailure) {
          const error = new Error('Required cart total or item section did not render');
          error.cartMayHaveChanged = true;
          throw error;
        }

        clearCartUpdateError();
        recordCartUpdate({
          type: 'cart-change',
          requestId: context.requestId,
          outcome: 'rendered',
          durationMs: Math.round(performance.now() - context.startedAt),
          retry: false,
        });
        this.updateLiveRegions(line, '');
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: state,
            variantId,
          });
        } catch (error) {
          reportError('cart-update-publish-failed', error);
        }
      } catch (error) {
        if (generation !== runtime.cartUpdateGeneration) return;
        runtime.diagnostics.cartUpdateFailures += 1;
        reportError('cart-quantity-update-failed', error);
        const timedOut = error?.name === 'AbortError' && context.timedOut;
        const totalMayBeStale =
          error?.cartMayHaveChanged ??
          (timedOut || error instanceof TypeError || cartMayHaveChanged);
        showCartUpdateError(
          this,
          error?.userMessage || "Couldn't update cart. Try again.",
          totalMayBeStale,
          true
        );
        this.resetQuantityInput(
          line,
          event?.target?.matches?.('.quantity__input') ? event.target : null
        );
        recordCartUpdate({
          type: 'cart-change',
          requestId: context.requestId,
          outcome: timedOut ? 'aborted-after-timeout' : 'failed',
          durationMs: Math.round(performance.now() - context.startedAt),
          retry: false,
        });
      } finally {
        window.clearTimeout(timeout);
        context.pending = false;
        if (runtime.cartUpdateContext === context) runtime.cartUpdateContext = null;
        clearCartQuantityIntent(event?.target);
        this.disableLoading();
      }
    };
  };

  const getTransactionButtons = (productInfo) => [
    ...new Set([
      ...productInfo.querySelectorAll('button[name="add"], .product-form__submit'),
      ...document.querySelectorAll(
        '[data-sticky-atc] button, [data-sticky-add-to-cart], .sticky-atc button, #sticky-atc button'
      ),
    ]),
  ];

  const captureVariantState = (productInfo) => {
    const variant = productInfo.getSelectedVariant?.(productInfo) || null;
    const hiddenInputs = [
      ...productInfo.querySelectorAll(
        `#product-form-${CSS.escape(productInfo.dataset.section || '')} input[name="id"],
         #product-form-installment-${CSS.escape(productInfo.dataset.section || '')} input[name="id"],
         product-form input[name="id"]`
      ),
    ];
    const variantId = String(hiddenInputs.find((input) => input.value)?.value || variant?.id || '');
    const selectedVariantNode = productInfo.querySelector(
      'variant-selects [data-selected-variant]'
    );
    const optionControls = [...productInfo.querySelectorAll('variant-selects input, variant-selects select')].map(
      (control) => ({
        control,
        checked: 'checked' in control ? control.checked : undefined,
        value: control.value,
        selectedIndex: 'selectedIndex' in control ? control.selectedIndex : undefined,
        ariaChecked: control.getAttribute('aria-checked'),
      })
    );
    const sectionId = productInfo.dataset.section || productInfo.sectionId;
    const visualNodes = [
      'price',
      'Inventory',
      'Sku',
      'Price-Per-Item',
      'Volume-Note',
      'Volume',
      'Quantity-Rules',
    ]
      .map((id) => productInfo.querySelector(`#${CSS.escape(id)}-${CSS.escape(sectionId || '')}`))
      .filter(Boolean)
      .map((node) => ({
        node,
        html: node.innerHTML,
        className: node.className,
        hidden: node.hidden,
      }));
    const stickyNodes = [
      ...document.querySelectorAll(
        '[data-selected-size], [data-sticky-variant], [data-sticky-atc-variant], .sticky-atc__variant, [data-sticky-price], .sticky-atc__price'
      ),
    ].map((node) => ({ node, text: node.textContent }));
    const buttons = getTransactionButtons(productInfo).map((button) => ({
      button,
      disabled: Boolean(button.disabled),
      ariaDisabled: button.getAttribute('aria-disabled'),
    }));

    return {
      variantId,
      label:
        variant?.public_title ||
        (Array.isArray(variant?.options) ? variant.options.filter(Boolean).join(' / ') : '') ||
        variantId,
      optionControls,
      hiddenInputs,
      selectedVariantNode,
      selectedVariantJson: selectedVariantNode?.textContent || '',
      visualNodes,
      stickyNodes,
      buttons,
    };
  };

  const setTransactionPending = (productInfo, pending, state) => {
    getTransactionButtons(productInfo).forEach((button) => {
      if (pending) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.gdcVariantPending = '';
        return;
      }

      const previous = state?.buttons?.find((entry) => entry.button === button);
      button.disabled = previous?.disabled ?? false;
      if (previous?.ariaDisabled === null || previous?.ariaDisabled === undefined) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', previous.ariaDisabled);
      }
      delete button.dataset.gdcVariantPending;
    });
    productInfo.toggleAttribute('data-gdc-variant-pending', pending);
  };

  const restoreVariantState = (productInfo, state) => {
    if (!state?.variantId || !state.hiddenInputs?.length) return false;

    state.optionControls.forEach(({ control, checked, value, selectedIndex, ariaChecked }) => {
      if (!control.isConnected) return;
      if (checked !== undefined) control.checked = checked;
      if (selectedIndex !== undefined) control.selectedIndex = selectedIndex;
      if (value !== undefined) control.value = value;
      if (ariaChecked === null) control.removeAttribute('aria-checked');
      else if (ariaChecked !== undefined) control.setAttribute('aria-checked', ariaChecked);
    });
    state.hiddenInputs.forEach((input) => {
      if (input.isConnected) input.value = state.variantId;
    });
    if (state.selectedVariantNode?.isConnected) {
      state.selectedVariantNode.textContent = state.selectedVariantJson;
    }
    state.visualNodes.forEach(({ node, html, className, hidden }) => {
      if (!node.isConnected) return;
      node.innerHTML = html;
      node.className = className;
      node.hidden = hidden;
    });
    state.stickyNodes.forEach(({ node, text }) => {
      if (node.isConnected) node.textContent = text;
    });
    setTransactionPending(productInfo, false, state);

    const connectedHiddenInputs = state.hiddenInputs.filter((input) => input.isConnected);
    return (
      connectedHiddenInputs.length > 0 &&
      connectedHiddenInputs.every((input) => String(input.value) === state.variantId)
    );
  };

  const disableUnconfirmedTransaction = (productInfo) => {
    getTransactionButtons(productInfo).forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      delete button.dataset.gdcVariantPending;
    });
    productInfo.removeAttribute('data-gdc-variant-pending');
    productInfo.setAttribute('data-gdc-variant-unconfirmed', '');
  };

  const showVariantUpdateError = (productInfo, state, rolledBack) => {
    const message = rolledBack
      ? `Unable to switch size. ${state.label} is still selected. Please try again.`
      : 'We could not confirm this size. Please retry.';
    productInfo.productForm?.handleErrorMessage?.(message);
    productInfo.dispatchEvent(
      new CustomEvent('gdc:product-info-error', {
        bubbles: true,
        detail: {
          recoverable: true,
          strategy: rolledBack ? 'rollback' : 'disable-transaction',
          confirmedVariantId: rolledBack ? state.variantId : null,
        },
      })
    );
  };

  const patchProductInfo = () => {
    const ProductInfo = customElements.get('product-info');
    if (!ProductInfo || ProductInfo.prototype.__gdcReliabilityPatched) return;

    const prototype = ProductInfo.prototype;
    prototype.__gdcReliabilityPatched = {
      version: PATCH_VERSION,
      strategy: 'prototype-method-replacement',
    };
    runtime.diagnostics.productPrototypePatches += 1;

    prototype.getSelectedVariant = function getSelectedVariant(productInfoNode) {
      if (!productInfoNode?.querySelector) return null;
      const value = productInfoNode.querySelector(
        'variant-selects [data-selected-variant]'
      )?.innerHTML;
      if (!value) return null;

      try {
        return JSON.parse(value);
      } catch (error) {
        reportError('variant-json-invalid', error);
        return null;
      }
    };

    prototype.updateVariantInputs = function updateVariantInputs(variantId) {
      this.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
      ).forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId ?? '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    };

    const originalUpdateQuantityRules = prototype.updateQuantityRules;
    prototype.updateQuantityRules = function updateQuantityRules(sectionId, html) {
      const updatedQuantityForm = html?.getElementById?.(`Quantity-Form-${sectionId}`);
      if (!updatedQuantityForm) {
        reportError(
          'quantity-rules-section-missing',
          new Error('Quantity form is missing from product section response')
        );
        return;
      }
      return originalUpdateQuantityRules.call(this, sectionId, html);
    };

    prototype.fetchQuantityRules = function fetchQuantityRules() {
      const currentVariantId = this.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;

      const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
      spinner?.classList.remove('hidden');

      fetch(
        `${this.dataset.url}?variant=${encodeURIComponent(currentVariantId)}&section_id=${encodeURIComponent(
          this.dataset.section
        )}`
      )
        .then((response) => {
          if (!response.ok) throw new Error(`Quantity rules returned HTTP ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          if (!responseText.trim()) throw new Error('Quantity rules response is empty');
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          this.updateQuantityRules(this.dataset.section, html);
        })
        .catch((error) => reportError('quantity-rules-update-failed', error))
        .finally(() => spinner?.classList.add('hidden'));
    };

    prototype.renderProductInfo = function renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.__gdcVariantRequestGeneration = (this.__gdcVariantRequestGeneration || 0) + 1;
      const generation = this.__gdcVariantRequestGeneration;
      const confirmedState = this.__gdcConfirmedVariantState || captureVariantState(this);
      this.__gdcConfirmedVariantState = confirmedState;
      this.removeAttribute('data-gdc-variant-unconfirmed');
      setTransactionPending(this, true, confirmedState);

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Product section returned HTTP ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          if (generation !== this.__gdcVariantRequestGeneration) return;
          if (!responseText.trim()) throw new Error('Product section response is empty');
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          if (!html.querySelector('product-info') && !html.querySelector('main')) {
            throw new Error('Product info selector is missing from section response');
          }
          const responseProductInfo =
            html.querySelector(`product-info[data-section="${CSS.escape(this.dataset.section || '')}"]`) ||
            html.querySelector('product-info');
          const responseVariant = this.getSelectedVariant(responseProductInfo);
          if (!responseVariant?.id) throw new Error('Confirmed variant ID is missing');

          callback(html);
          if (generation !== this.__gdcVariantRequestGeneration) return;

          const currentVariantId = String(
            this.querySelector('product-form input[name="id"], form[action*="/cart/add"] input[name="id"]')
              ?.value || ''
          );
          if (currentVariantId !== String(responseVariant.id)) {
            throw new Error('Product option UI and cart payload variant IDs do not match');
          }

          this.__gdcConfirmedVariantState = captureVariantState(this);
          setTransactionPending(this, false, this.__gdcConfirmedVariantState);
          this.dispatchEvent(
            new CustomEvent('gdc:variant-confirmed', {
              bubbles: true,
              detail: { variantId: currentVariantId },
            })
          );
          document.querySelector(`#${CSS.escape(targetId)}`)?.focus();
        })
        .catch((error) => {
          if (
            error.name === 'AbortError' ||
            generation !== this.__gdcVariantRequestGeneration
          ) {
            return;
          }
          this.pendingRequestUrl = null;
          reportError('product-info-update-failed', error);
          const rolledBack = restoreVariantState(this, confirmedState);
          if (!rolledBack) disableUnconfirmedTransaction(this);
          showVariantUpdateError(this, confirmedState, rolledBack);
        });
    };

    document.querySelectorAll('product-info').forEach((productInfo) => {
      productInfo.__gdcConfirmedVariantState ||= captureVariantState(productInfo);
    });
  };

  const patchCheckoutState = () => {
    if (runtime.checkoutListenerBound) return;
    runtime.checkoutListenerBound = true;
    runtime.diagnostics.checkoutListeners += 1;

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('#CartDrawer-Checkout');
        if (!button || button.disabled) return;
        if (button.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        button.classList.add('is-loading');
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        window.setTimeout(() => {
          button.disabled = true;
        }, 0);
      },
      true
    );

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      document.querySelectorAll('#CartDrawer-Checkout.is-loading').forEach((button) => {
        button.classList.remove('is-loading');
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
      });
    });
    runtime.diagnostics.pageshowListeners += 1;
  };

  runtime.patchPromisesScheduled = true;
  customElements.whenDefined('cart-drawer').then(patchCartDrawer).catch((error) => {
    reportError('cart-drawer-definition-failed', error);
  });
  customElements.whenDefined('cart-items').then(() => {
    patchCartItems();
    bindCartQuantityIntentListener();
  }).catch((error) => {
    reportError('cart-items-definition-failed', error);
  });
  customElements.whenDefined('product-info').then(patchProductInfo).catch((error) => {
    reportError('product-info-definition-failed', error);
  });
  patchCheckoutState();
})();
